import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Settings,
  BarChart3,
  UserPlus,
  Menu,
  X,
  Package,
  Image,
  LogOut,
  ChevronDown,
  User,
  CheckCircle2,
  Wrench,
  Inbox,
  IndianRupee,
  MessageSquare,
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Logo } from '@/components/common/Logo';
import { cn } from '@/lib/utils';

const sidebarItems = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: LayoutDashboard,
    exact: true,
  },
  {
    title: 'User Management',
    href: '/admin/users',
    icon: Users,
  },
  {
    title: 'Create User',
    href: '/admin/users/create',
    icon: UserPlus,
  },
  {
    title: 'Orders',
    href: '/admin/orders',
    icon: Package,
  },
  {
    title: 'Partner Earnings',
    href: '/admin/vendor-earnings',
    icon: IndianRupee,
  },
  {
    title: 'Approvals',
    href: '/admin/approvals',
    icon: CheckCircle2,
    children: [
      {
          title: 'Partner Approvals',
        href: '/admin/approvals/vendors',
        icon: Users,
      },
    ],
  },
  {
    title: 'Service Categories',
    href: '/admin/service-categories',
    icon: Wrench,
  },
  {
    title: 'Product Categories',
    href: '/admin/product-categories',
    icon: Package,
  },
  {
    title: 'All Products',
    href: '/admin/products',
    icon: Package,
  },
  {
    title: 'Partner category requests',
    href: '/admin/vendor-category-requests',
    icon: Inbox,
  },
  {
    title: 'Service Requests',
    href: '/admin/service-requests',
    icon: Wrench,
  },
  {
    title: 'Banners',
    href: '/admin/banners',
    icon: Image,
  },
  {
    title: 'Analytics',
    href: '/admin/analytics',
    icon: BarChart3,
  },
  {
    title: 'Support Tickets',
    href: '/admin/support',
    icon: MessageSquare,
  },
  {
    title: 'Settings',
    href: '/admin/settings',
    icon: Settings,
  },
];

export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const { user, logout, getUserFullName, getUserInitials } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActiveRoute = (href: string, exact = false) => {
    if (exact) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform border-r border-border bg-card transition-transform duration-200 ease-in-out lg:static lg:inset-0 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Sidebar Header */}
          <div className="flex h-16 items-center justify-between border-b border-border px-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex min-w-0 flex-col">
                <span className="text-base font-semibold leading-tight text-foreground">
                  Admin Panel
                </span>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="flex-shrink-0 rounded-md p-1.5 transition-colors hover:bg-muted lg:hidden"
              aria-label="Close sidebar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-4">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const active = isActiveRoute(item.href, item.exact);
              const hasChildren = item.children && item.children.length > 0;
              const isApprovalsOpen =
                hasChildren &&
                (active ||
                  item.children?.some((child) => isActiveRoute(child.href)));

              if (hasChildren) {
                const shouldShowChildren = approvalsOpen || isApprovalsOpen;
                return (
                  <div key={item.href} className="space-y-1">
                    <div
                      onClick={() => setApprovalsOpen(!approvalsOpen)}
                      className={cn(
                        'flex cursor-pointer items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        active || isApprovalsOpen
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Icon className="mr-3 h-4 w-4 flex-shrink-0" />
                      <span className="flex-1">{item.title}</span>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform',
                          shouldShowChildren && 'rotate-180'
                        )}
                      />
                    </div>
                    {shouldShowChildren && (
                      <div className="ml-6 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = isActiveRoute(child.href);
                          return (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              className={cn(
                                'flex items-center rounded-md px-3 py-2 text-sm transition-colors',
                                childActive
                                  ? 'bg-primary/20 font-medium text-primary'
                                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                              )}
                            >
                              <ChildIcon className="mr-3 h-4 w-4 flex-shrink-0" />
                              {child.title}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  {item.title}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="border-t border-border p-4">
            <div className="flex items-center space-x-3">
              {user?.profile?.avatar ? (
                <img
                  src={user.profile.avatar}
                  alt={getUserFullName()}
                  className="h-8 w-8 rounded-full border-2 border-primary/20 object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                  {getUserInitials()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {getUserFullName()}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {user?.email}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-border bg-card px-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="rounded-md p-1 hover:bg-muted lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Logo - Single icon on mobile, full logo on desktop */}
            <Link to="/admin" className="md:hidden">
              <Logo variant="single" size="sm" linkToHome={false} />
            </Link>
            <Link to="/admin" className="hidden md:block">
              <Logo variant="full" size="sm" linkToHome={false} />
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {/* Theme toggle */}
            <ThemeToggle size="sm" />

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 rounded-md p-2 hover:bg-muted"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                  {getUserInitials()}
                </div>
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium text-foreground">
                    {getUserFullName()}
                  </p>
                  <p className="text-xs text-muted-foreground">Administrator</p>
                </div>
                <ChevronDown className="h-3 w-3" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-48 rounded-md border border-border bg-card shadow-lg">
                  <div className="py-1">
                    <Link
                      to="/admin/settings"
                      className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
